import { useState, useEffect } from 'react';
import { useUserPreferences, useStoreActions, useSettingsStore } from '@/store/useStore';
import { Calendar, Clock, MapPin, FileText, MessageSquare, User, Search, Bell, Bus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DataProcessor } from '@/utils/dataProcessor';
import { findUserProgram } from '@/utils/programMatching';
import { getOrderedDays } from '@/utils/dayUtils';
import { applyTimetableProfile } from '@/utils/timetableProfiles';
import Logo from '@/components/Logo';
import { DesktopNav } from '@/components/DesktopNav';
import { useDashboard } from '@/contexts/DashboardContext';
import SemesterMigrationNotice from '@/components/SemesterMigrationNotice';
import GraduationNotice from '@/components/GraduationNotice';
import { useGraduationNotice, useHasGraduated } from '@/hooks/useSemesterCompletion';
import { LostAndFoundShortcut } from '@/components/LostAndFoundShortcut';

// ─── Types ────────────────────────────────────────────────────────────────────
interface TimetableProfileControl {
  officialStartDate?: string;
  officialEndDate?: string;
  predictedStartDate?: string;
  predictedEndDate?: string;
}
interface TimetableProfileControlFile {
  profiles?: Record<string, TimetableProfileControl>;
}

// ─── Small shared primitives ──────────────────────────────────────────────────
function SectionHeader({ title, meta }: { title: string; meta?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <span style={{ fontFamily: "'Instrument Serif',serif", fontSize: 20, fontWeight: 400, letterSpacing: '-.01em' }}>
        {title}
      </span>
      {meta && (
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.14em', color: 'rgba(255,255,255,.3)' }}>
          {meta}
        </span>
      )}
    </div>
  );
}

function NavIconBtn({ children, onClick, badge }: { children: React.ReactNode; onClick?: () => void; badge?: number }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 34, height: 34, borderRadius: 10, flexShrink: 0,
        background: hov ? 'rgba(255,255,255,.09)' : 'rgba(255,255,255,.05)',
        border: '1px solid rgba(255,255,255,.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: hov ? '#fff' : 'rgba(255,255,255,.5)',
        cursor: 'pointer', transition: '.2s', position: 'relative',
      }}
    >
      {children}
      {badge && badge > 0 ? (
        <span style={{
          position: 'absolute', top: -3, right: -3,
          width: 14, height: 14, borderRadius: '50%',
          background: '#EF4444', border: '2px solid #0A0A0A',
          fontSize: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, color: '#fff',
        }}>{badge}</span>
      ) : null}
    </button>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; border: string; color: string; label: string }> = {
    ongoing:   { bg: 'rgba(20,184,166,.12)',   border: 'rgba(20,184,166,.3)',   color: '#14B8A6', label: 'LIVE'      },
    upcoming:  { bg: 'rgba(96,165,250,.1)',    border: 'rgba(96,165,250,.25)',  color: '#60A5FA', label: 'NEXT'      },
    completed: { bg: 'rgba(255,255,255,.05)',  border: 'rgba(255,255,255,.1)',  color: 'rgba(255,255,255,.3)', label: 'DONE' },
    cancelled: { bg: 'rgba(239,68,68,.1)',     border: 'rgba(239,68,68,.25)',   color: 'rgba(239,68,68,.7)',   label: 'OFF'  },
  };
  const s = styles[status] ?? styles.upcoming;
  return (
    <span style={{
      fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.08em',
      padding: '3px 7px', borderRadius: 4,
      background: s.bg, border: `1px solid ${s.border}`, color: s.color,
      fontWeight: 500, whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function HomePage() {
  const dashboard = useDashboard();
  const userPreferences = useUserPreferences();
  const { initializeData } = useStoreActions();
  const { preferences } = useSettingsStore();
  const graduation = useGraduationNotice(userPreferences);
  const { graduated: hasGraduated } = useHasGraduated(userPreferences);

  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [todayClasses, setTodayClasses] = useState<any[]>([]);
  const [showMigrationNotice, setShowMigrationNotice] = useState(false);
  const [fullWeekTimetable, setFullWeekTimetable] = useState<Record<string, any[]>>({});
  const [selectedDayView, setSelectedDayView] = useState(new Date().toLocaleDateString('en-US', { weekday: 'long' }));
  const [ramadanDateLabel, setRamadanDateLabel] = useState('Ramadan 1st, 2026');
  const [ramadanWindow, setRamadanWindow] = useState<{ start?: Date; end?: Date }>({});

  useEffect(() => {
    setCurrentDateTime(new Date());
    const timer = setInterval(() => setCurrentDateTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const pakistanTime = new Date(currentDateTime.toLocaleString('en-US', { timeZone: 'Asia/Karachi' }));
  const selectedDay = pakistanTime.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'Asia/Karachi' });
  const currentTime = pakistanTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Karachi' });

  const parseTimeToMinutes = (timeStr: string) => {
    const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return 0;
    let hours = parseInt(match[1]);
    const minutes = parseInt(match[2]);
    const period = match[3].toUpperCase();
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return hours * 60 + minutes;
  };

  const currentMinutes = parseTimeToMinutes(currentTime);

  const getOrdinal = (n: number): string => {
    const v = n % 100;
    if (v >= 11 && v <= 13) return `${n}th`;
    switch (n % 10) { case 1: return `${n}st`; case 2: return `${n}nd`; case 3: return `${n}rd`; default: return `${n}th`; }
  };

  // ── Ramadan window ──
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch('/timetable-profile-control.json', { cache: 'no-store' });
        if (!res.ok) return;
        const payload = await res.json() as TimetableProfileControlFile;
        const profile = payload.profiles?.IMS_Bachelors_Ramadan_2026;
        if (!profile) return;
        const startDate = profile.officialStartDate || profile.predictedStartDate || '2026-02-19';
        const endDate   = profile.officialEndDate   || profile.predictedEndDate   || '2026-03-21';
        setRamadanWindow({
          start: startDate ? new Date(`${startDate}T00:00:00`) : undefined,
          end:   endDate   ? new Date(`${endDate}T00:00:00`)   : undefined,
        });
      } catch { /* keep fallback */ }
    };
    load();
  }, []);

  useEffect(() => {
    const today = new Date(pakistanTime); today.setHours(0,0,0,0);
    if (!ramadanWindow.start) { setRamadanDateLabel('Ramadan 1st, 2026'); return; }
    const start = new Date(ramadanWindow.start); start.setHours(0,0,0,0);
    const end   = ramadanWindow.end ? new Date(ramadanWindow.end) : undefined;
    if (end) end.setHours(0,0,0,0);
    if (!(today >= start && (!end || today <= end))) { setRamadanDateLabel(''); return; }
    const day = Math.floor((today.getTime() - start.getTime()) / 86400000) + 1;
    setRamadanDateLabel(`Ramadan ${getOrdinal(Math.max(1, day))}, ${today.getFullYear()}`);
  }, [currentDateTime, ramadanWindow.start, ramadanWindow.end]);

  const getNextUpcomingClass = () => {
    const updated = todayClasses.map(cls => {
      let status = 'upcoming';
      if (currentMinutes >= cls.startMinutes && currentMinutes < cls.endMinutes) status = 'ongoing';
      else if (currentMinutes >= cls.endMinutes) status = 'completed';
      return { ...cls, status };
    });
    const upcoming = updated.filter(c => c.status === 'upcoming');
    if (upcoming.length > 0) {
      const next = upcoming[0];
      const diff = next.startMinutes - currentMinutes;
      const h = Math.floor(diff / 60), m = diff % 60;
      return { ...next, timeUntil: diff <= 0 ? 'Now' : h > 0 ? `${h}h ${m}m` : `${m} min`, isToday: true };
    }
    if (userPreferences) {
      const dp = DataProcessor.getInstance();
      const userProgram = findUserProgram(dp.getAllPrograms(), userPreferences);
      if (userProgram) {
        const { schedule } = applyTimetableProfile(userProgram.schedule, { institute: 'Institute of Management Sciences', department: userProgram.department, program: userProgram.program, referenceDate: pakistanTime });
        const days = getOrderedDays(preferences.weekStartsOn, true);
        const idx = days.indexOf(selectedDay);
        for (let i = 1; i <= 7; i++) {
          const nextDay = days[(idx + i) % 7];
          if (schedule[nextDay]?.length > 0) {
            const fc = schedule[nextDay][0];
            return { time: fc.time, course: fc.course_name || fc.course, room: fc.location, instructor: fc.instructor || fc.teacher, timeUntil: i === 1 ? 'Tomorrow' : `${i} days`, day: nextDay, isToday: false };
          }
        }
      }
    }
    return null;
  };

  const nextClass = getNextUpcomingClass();

  useEffect(() => {
    const loadData = async () => {
      await initializeData();
      if (userPreferences) {
        const dp = DataProcessor.getInstance();
        const userProgram = findUserProgram(dp.getAllPrograms(), userPreferences);
        const effectiveSchedule = userProgram ? applyTimetableProfile(userProgram.schedule, { institute: 'Institute of Management Sciences', department: userProgram.department, program: userProgram.program, referenceDate: pakistanTime }).schedule : null;

        if (userProgram && effectiveSchedule?.[selectedDay]) {
          const cancelled = new Set(JSON.parse(localStorage.getItem('cancelledClasses') || '[]').filter((id: string) => id.startsWith(`${userProgram.id}-`)));
          const classes = effectiveSchedule[selectedDay].map((course: any, i: number) => {
            const [st, et] = course.time.split('-').map((t: string) => t.trim());
            const startMinutes = parseTimeToMinutes(st), endMinutes = parseTimeToMinutes(et);
            const classId = `${userProgram.id}-${selectedDay}-${i}`;
            const isCancelled = cancelled.has(classId);
            let status = 'upcoming';
            if (isCancelled) status = 'cancelled';
            else if (currentMinutes >= startMinutes && currentMinutes < endMinutes) status = 'ongoing';
            else if (currentMinutes >= endMinutes) status = 'completed';
            return { id: classId, time: course.time, startMinutes, endMinutes, course: course.course_name || course.course, room: course.location, instructor: course.instructor || course.teacher, status, isCancelled };
          }).sort((a: any, b: any) => a.startMinutes - b.startMinutes);
          setTodayClasses(classes);
          setShowMigrationNotice(false);

          if (preferences.showFullTimetableOnDashboard) {
            const days = getOrderedDays(preferences.weekStartsOn, preferences.showWeekends);
            const wt: Record<string, any[]> = {};
            days.forEach((day: string) => {
              wt[day] = (effectiveSchedule[day] || []).map((c: any) => ({ time: c.time, course: c.course_name || c.course, room: c.location, instructor: c.instructor || c.teacher }))
                .sort((a: any, b: any) => parseTimeToMinutes(a.time.split('-')[0].trim()) - parseTimeToMinutes(b.time.split('-')[0].trim()));
            });
            setFullWeekTimetable(wt);
          }
        } else if (!userProgram) {
          setShowMigrationNotice(true);
        }
      }
      setIsLoading(false);
    };
    loadData();
  }, [userPreferences, selectedDay, initializeData, currentMinutes, preferences.showFullTimetableOnDashboard, preferences.weekStartsOn, preferences.showWeekends]);

  // ── Loading skeleton ──
  if (isLoading) {
    return (
      <div style={{ background: '#0A0A0A', minHeight: '100vh', padding: '20px 24px' }}>
        {[80, 120, 180, 100].map((h, i) => (
          <div key={i} style={{ background: 'rgba(255,255,255,.04)', borderRadius: 14, height: h, marginBottom: 12,
            animation: 'pulse 1.5s ease-in-out infinite', animationDelay: `${i * 0.1}s` }} />
        ))}
        <style>{`@keyframes pulse{0%,100%{opacity:.4}50%{opacity:.8}}`}</style>
      </div>
    );
  }

  // Graduation is checked first: a graduated batch drops out of the current
  // timetable data, which also trips showMigrationNotice. Without this order
  // they'd be told to re-pick a program instead of being congratulated.
  if (graduation.show && graduation.semesterLabel) {
    return (
      <GraduationNotice
        userName={userPreferences?.name}
        semesterLabel={graduation.semesterLabel}
        onDismiss={graduation.dismiss}
      />
    );
  }

  if (showMigrationNotice && !hasGraduated) {
    return <SemesterMigrationNotice userName={userPreferences?.name} />;
  }

  const unreadCount = dashboard ? dashboard.generateNotifications().filter((n: any) => !dashboard.readNotifications.has(n.id)).length : 0;

  return (
    <div style={{ background: '#0A0A0A', fontFamily: "'Outfit',sans-serif", color: '#fff', minHeight: '100vh' }} className="pb-24 lg:pb-0">

      {/* ── Nav (Mobile/Tablet Only) ─────────────────────────────────────────── */}
      <header className="lg:hidden" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 24px',
        borderBottom: '1px solid rgba(255,255,255,.07)',
        background: '#0A0A0A', position: 'sticky', top: 0, zIndex: 40,
      }}>
        <Logo size="sm" clickable navigateTo="/dashboard/home" />
        <DesktopNav />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <NavIconBtn onClick={() => dashboard?.setShowSearch(true)}>
            <Search size={15} aria-hidden />
          </NavIconBtn>
          <NavIconBtn
            badge={unreadCount}
            onClick={() => {
              if (dashboard) {
                dashboard.setShowNotifications(!dashboard.showNotifications);
                if (!dashboard.showNotifications) setTimeout(() => dashboard.markNotificationsAsRead(), 100);
              }
            }}
          >
            <Bell size={15} aria-hidden />
          </NavIconBtn>
          <button
            onClick={() => dashboard?.setShowUserMenu(!dashboard?.showUserMenu)}
            style={{
              width: 34, height: 34, borderRadius: 10, flexShrink: 0,
              background: 'rgba(20,184,166,.15)',
              border: '1px solid rgba(20,184,166,.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#14B8A6', fontFamily: "'JetBrains Mono',monospace",
              fontSize: 11, fontWeight: 500, cursor: 'pointer',
            }}
          >
            {userPreferences?.name ? userPreferences.name.charAt(0).toUpperCase() : 'U'}
          </button>
        </div>
      </header>

      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 28 }}>

        {/* ── Greeting card ───────────────────────────── */}
        <div style={{
          background: 'linear-gradient(135deg,rgba(20,184,166,.12) 0%,rgba(6,182,212,.07) 100%)',
          border: '1px solid rgba(20,184,166,.18)',
          borderRadius: 16, padding: '20px 22px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: -30, right: -30, width: 140, height: 140, background: 'radial-gradient(circle,rgba(20,184,166,.18) 0%,transparent 65%)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            {ramadanDateLabel && (
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.14em', color: 'rgba(20,184,166,.7)', marginBottom: 8 }}>
                {ramadanDateLabel.toUpperCase()}
              </div>
            )}
            <div style={{ fontFamily: "'Instrument Serif',serif", fontSize: 28, fontWeight: 400, letterSpacing: '-.02em', lineHeight: 1, marginBottom: 4 }}>
              {selectedDay}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', fontWeight: 300 }}>
              {pakistanTime.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'Asia/Karachi' })}
            </div>
          </div>

          <div style={{ textAlign: 'right', position: 'relative', zIndex: 1 }}>
            <div style={{ fontFamily: "'Instrument Serif',serif", fontSize: 32, fontWeight: 400, letterSpacing: '-.02em', lineHeight: 1 }}>
              {currentTime}
            </div>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.1em', color: 'rgba(255,255,255,.3)', marginTop: 4 }}>
              PAKISTAN TIME
            </div>
          </div>
        </div>

        {/* ── Full week timetable (optional) ──────────── */}
        {preferences.showFullTimetableOnDashboard && Object.keys(fullWeekTimetable).length > 0 && (
          <div>
            <SectionHeader title="Full Week" meta="SCHEDULE" />

            {/* Day pills */}
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 12, marginBottom: 16 }}>
              {Object.keys(fullWeekTimetable).map(day => (
                <button
                  key={day}
                  onClick={() => setSelectedDayView(day)}
                  style={{
                    padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
                    fontFamily: "'JetBrains Mono',monospace", fontSize: 10, letterSpacing: '.08em',
                    whiteSpace: 'nowrap', border: 'none', transition: '.2s',
                    background: selectedDayView === day ? 'rgba(20,184,166,.15)' : 'rgba(255,255,255,.04)',
                    color: selectedDayView === day ? '#14B8A6' : 'rgba(255,255,255,.4)',
                    borderBottom: selectedDayView === day ? '1px solid rgba(20,184,166,.35)' : '1px solid transparent',
                  }}
                >
                  {day.substring(0, 3).toUpperCase()}
                </button>
              ))}
            </div>

            {fullWeekTimetable[selectedDayView]?.length === 0 ? (
              <EmptyState icon={<Calendar size={24} />} text={`No classes on ${selectedDayView}`} />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {fullWeekTimetable[selectedDayView].map((cls: any, i: number) => (
                  <WeekClassCard key={i} cls={cls} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Next class ──────────────────────────────── */}
        {nextClass && (
          <div>
            <SectionHeader title="Next Up" meta={nextClass.isToday ? 'TODAY' : nextClass.day?.toUpperCase()} />
            <div style={{
              background: 'rgba(20,184,166,.08)',
              border: '1px solid rgba(20,184,166,.2)',
              borderRadius: 14, padding: '20px 22px',
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', bottom: -40, right: -20, width: 160, height: 160, background: 'radial-gradient(circle,rgba(20,184,166,.12) 0%,transparent 65%)', pointerEvents: 'none' }} />
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.14em', color: 'rgba(20,184,166,.7)' }}>NEXT CLASS</span>
                  <span style={{
                    background: 'rgba(20,184,166,.15)', border: '1px solid rgba(20,184,166,.25)',
                    borderRadius: 20, padding: '4px 10px',
                    fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: '#14B8A6', letterSpacing: '.08em',
                  }}>In {nextClass.timeUntil}</span>
                </div>
                <div style={{ fontFamily: "'Instrument Serif',serif", fontSize: 24, fontWeight: 400, letterSpacing: '-.02em', lineHeight: 1, marginBottom: 14 }}>
                  {nextClass.course}
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  {[
                    { Icon: User,    text: nextClass.instructor },
                    { Icon: Clock,   text: nextClass.time },
                    { Icon: MapPin,  text: nextClass.room },
                  ].filter(m => m.text).map(({ Icon, text }) => (
                    <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'rgba(255,255,255,.5)', fontWeight: 300 }}>
                      <Icon size={13} color="#14B8A6" aria-hidden /> {text}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Today's schedule ────────────────────────── */}
        <div>
          <SectionHeader title="Today's Schedule" meta={todayClasses.length > 0 ? `${todayClasses.length} CLASSES` : undefined} />

          {todayClasses.length === 0 ? (
            <EmptyState icon={<Calendar size={24} />} text="No classes scheduled for today" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {todayClasses.map(cls => (
                <ScheduleCard key={cls.id} cls={cls} />
              ))}
            </div>
          )}
        </div>

        {/* ── Quick access ────────────────────────────── */}
        <div>
          <SectionHeader title="Quick Access" />
          <div style={{ marginBottom: 10 }}><LostAndFoundShortcut /></div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
            {[
              { Icon: Bus,          label: 'Bus Routes', href: '/dashboard/bus'      },
              { Icon: Calendar,     label: 'Calendar',   href: '/dashboard/calendar' },
              { Icon: FileText,     label: 'Exams',      href: '/dashboard/exams'    },
              { Icon: MessageSquare,label: 'Feedback',   href: '/dashboard/feedback' },
            ].map(({ Icon, label, href }) => (
              <QuickAccessBtn key={href} Icon={Icon} label={label} href={href} />
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function EmptyState({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.07)',
      borderRadius: 14, padding: '32px 24px', textAlign: 'center',
    }}>
      <div style={{ color: 'rgba(255,255,255,.2)', marginBottom: 10, display: 'flex', justifyContent: 'center' }}>{icon}</div>
      <p style={{ fontSize: 12, color: 'rgba(255,255,255,.3)', fontWeight: 300 }}>{text}</p>
    </div>
  );
}

function ScheduleCard({ cls }: { cls: any }) {
  const [startTime] = cls.time.split('-');
  const endPart = cls.time.split('-')[1]?.trim() || '';

  return (
    <div style={{
      background: cls.status === 'ongoing' ? 'rgba(20,184,166,.07)' : 'rgba(255,255,255,.03)',
      border: `1px solid ${cls.status === 'ongoing' ? 'rgba(20,184,166,.2)' : 'rgba(255,255,255,.07)'}`,
      borderRadius: 12, padding: '14px 16px',
      display: 'flex', alignItems: 'flex-start', gap: 14,
      opacity: cls.status === 'completed' || cls.status === 'cancelled' ? .45 : 1,
      transition: 'opacity .2s',
    }}>
      {/* Time column */}
      <div style={{ minWidth: 52, textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: cls.status === 'ongoing' ? '#14B8A6' : 'rgba(255,255,255,.6)', lineHeight: 1 }}>
          {startTime.trim()}
        </div>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: 'rgba(255,255,255,.25)', marginTop: 2 }}>
          {endPart}
        </div>
      </div>

      {/* Divider */}
      <div style={{
        width: 1, alignSelf: 'stretch', margin: '2px 0', flexShrink: 0,
        background: cls.status === 'ongoing' ? 'rgba(20,184,166,.4)' : 'rgba(255,255,255,.08)',
      }} />

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 500, lineHeight: 1.2, marginBottom: 6,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          textDecoration: cls.isCancelled ? 'line-through' : 'none',
        }}>
          {cls.course}
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {cls.instructor && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'rgba(255,255,255,.35)', fontWeight: 300 }}>
              <User size={11} color="rgba(20,184,166,.6)" aria-hidden /> {cls.instructor}
            </div>
          )}
          {cls.room && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'rgba(255,255,255,.35)', fontWeight: 300 }}>
              <MapPin size={11} color="rgba(20,184,166,.6)" aria-hidden /> {cls.room}
            </div>
          )}
        </div>
      </div>

      {/* Badge */}
      <div style={{ flexShrink: 0 }}>
        <StatusBadge status={cls.status} />
      </div>
    </div>
  );
}

function WeekClassCard({ cls }: { cls: any }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.07)',
      borderLeft: '2px solid #14B8A6',
      borderRadius: 12, padding: '14px 16px',
    }}>
      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>{cls.course}</div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {[
          { Icon: Clock,  text: cls.time },
          { Icon: MapPin, text: cls.room },
          { Icon: User,   text: cls.instructor },
        ].filter(m => m.text).map(({ Icon, text }) => (
          <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'rgba(255,255,255,.35)', fontWeight: 300 }}>
            <Icon size={11} color="rgba(20,184,166,.6)" aria-hidden /> {text}
          </div>
        ))}
      </div>
    </div>
  );
}

function QuickAccessBtn({ Icon, label, href }: { Icon: any; label: string; href: string }) {
  const [hov, setHov] = useState(false);
  return (
    <Link to={href} style={{ textDecoration: 'none' }}>
      <div
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          background: hov ? 'rgba(20,184,166,.06)' : 'rgba(255,255,255,.03)',
          border: `1px solid ${hov ? 'rgba(20,184,166,.25)' : 'rgba(255,255,255,.07)'}`,
          borderRadius: 12, padding: '18px 12px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
          cursor: 'pointer', transition: '.2s',
        }}
      >
        <div style={{
          width: 32, height: 32, borderRadius: 9,
          background: 'rgba(20,184,166,.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#14B8A6', transform: hov ? 'scale(1.1)' : 'scale(1)', transition: 'transform .2s',
        }}>
          <Icon size={15} aria-hidden />
        </div>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,.5)', textAlign: 'center' }}>{label}</span>
      </div>
    </Link>
  );
}
